/**
 * 工地时区的可选项。
 *
 * **不是完整的 IANA 库**（那有四百多个）。客户在北美，列他真正会用到的那些；
 * 要加别的地区直接往这里加一行，两个表单都会跟着变 ——
 * 改之前这份列表写死在「工地设置」里，而**新建工地的表单压根没有时区字段**，
 * 建出来的工地一律落成 `America/Los_Angeles`。客户人在卡尔加里，
 * 他建的每个工地默认都是错的，还得自己知道要再进设置里改一次（CR-027）。
 *
 * 按经度从西往东排，找起来比字母序快。
 */
export interface TimeZoneOption {
  value: string;
  /** 括号里写城市名：`America/Edmonton` 这种 id 认不出来的人不少 */
  cities?: string;
}

export const TIME_ZONES: readonly TimeZoneOption[] = [
  { value: 'America/Vancouver', cities: '温哥华' },
  { value: 'America/Los_Angeles' },
  // 卡尔加里没有自己的时区 id，整个艾伯塔省用 America/Edmonton。
  // 它和 America/Denver 当前偏移相同，但夏令时规则各归各国，不能互相顶替。
  { value: 'America/Edmonton', cities: '卡尔加里 / 埃德蒙顿' },
  { value: 'America/Denver' },
  { value: 'America/Regina', cities: '萨斯喀彻温，不用夏令时' },
  { value: 'America/Winnipeg', cities: '温尼伯' },
  { value: 'America/Chicago' },
  { value: 'America/Toronto', cities: '多伦多 / 渥太华' },
  { value: 'America/New_York' },
  { value: 'America/Halifax', cities: '哈利法克斯' },
  { value: 'America/St_Johns', cities: '圣约翰斯，差半小时' },
  { value: 'Asia/Shanghai' },
];

/** 兜底时区。列表里第一个不一定合适，所以单独写明。 */
export const FALLBACK_TIME_ZONE = 'America/Los_Angeles';

/**
 * 新建工地时的默认时区：**优先用建工地这个人所在的时区**。
 *
 * 建工地的人多半就在那个工地所在的地区（客户人在卡尔加里，建的是卡尔加里的工地）。
 * 猜错也没关系 —— 下拉就在旁边，改一下就是；但猜对的那部分人一步都不用动。
 *
 * 浏览器给出的时区不在列表里时退回兜底值，不能塞一个下拉里没有的值进去：
 * `<Select>` 的 value 对不上任何 option 时会显示成空白，看着像没填。
 */
export function defaultTimeZone(): string {
  try {
    const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIME_ZONES.some((tz) => tz.value === local) ? local : FALLBACK_TIME_ZONE;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}

/** 下拉里显示的文字 */
export function timeZoneLabel(tz: TimeZoneOption): string {
  return tz.cities ? `${tz.value}（${tz.cities}）` : tz.value;
}
